import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type { Log } from '@blakeelearning/log'
import type RouterService from '@ember/routing/router-service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type WritingLegendsSubmissionService from 'teacher-ui-client/services/writing-legends-submission'
import type WritingLegendsCatalogueService from 'teacher-ui-client/services/writing-legends-catalogue'
import type { LongWritesActivity } from 'teacher-ui-client/subscription-type/long-writes/previews/route'
import type SessionService from 'teacher-ui-client/services/session'

interface Params extends Record<string, unknown> {
  activity_id: string
  student_id: string
  snapshot_id: string
}

export interface LongWriteSubmission {
  score: number
  words: number
  reflection: string
  updated_date: number
  result_id: string
  answer_snapshots: {
    answer_text: string
    plain_text: string
  }[]
  legends_feedback: {
    target_word_count: number
  }[]
}

interface Student {
  first_name: string
  last_name: string
  username: string
  user_id: string
  gradePosition: number
}

export interface LongWriteAnswer {
  activityAnswerId: string
  activityResultId: string
  blockId: string
  questionType: string
  answerText: string
  plainText: string
  words: number
  lastUpdatedDate: string
}

interface LongWriteResult {
  activityId: string
  activityTemplate: string
  displayCategory: string
  displayTopic: string
  activityResultId: string
  userTitle: string
  userTargetWordCount: number
  lastUpdatedDate: string
  answers: LongWriteAnswer[]
}

export default class LongWritesSubmissionRoute extends Route {
  @service session!: SessionService

  @service writingLegendsSubmission!: WritingLegendsSubmissionService

  @service writingLegendsCatalogue!: WritingLegendsCatalogueService

  @service log!: Log

  @service router!: RouterService

  async model(params: Params) {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    // if its not writing subscription type, redirect to home
    if (subscriptionType !== SubscriptionType.Writing) {
      this.log.error('view writing page is only available for writing subscription type')
      void this.router.replaceWith('subscription-type.home')
      return { subscriptionType }
    }

    const submission = await this.writingLegendsSubmission.fetchData<LongWriteSubmission>(
      this.writingLegendsSubmission.buildSubmissionUrl(params.student_id, params.snapshot_id),
    )

    if (!submission) {
      this.log.error('Failed to fetch writing legends submission data')
      return { subscriptionType }
    }

    const [result, activity, student] = await Promise.all([
      this.writingLegendsSubmission.fetchData<LongWriteResult>(
        this.writingLegendsSubmission.buildResultUrl(
          this.session.currentSchool.id,
          params.activity_id,
          submission?.result_id,
        ),
      ),
      this.writingLegendsCatalogue.fetchData<LongWritesActivity>(
        this.writingLegendsCatalogue.buildActivityUrl(params.activity_id),
      ),
      this.writingLegendsSubmission.fetchData<{ data: Student }>(
        this.writingLegendsSubmission.buildStudentProfileUrl(params.student_id),
      ),
    ])

    return {
      params,
      submission,
      result,
      activity,
      student: student?.data,
    }
  }
}
